import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Heading, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { useIsMobile } from '@wix/gg-common-lib/hooks';
import { useAcceptPaymentsSetup, usePrimarySiteUrlInfo } from 'api';
import { buildDashboardUrl } from 'utils/dashboard';
import { useMsId, useSelectedStoreName } from 'stores/appStore';
import { getProviderConfig, getSuccessUrl } from 'config/providers';
import { postPurchaseBi } from './postPurchaseBi';
import type { PanelId } from './types';
import { getDisplayUrl, getPostPurchaseSteps, getSuggestedDomain, hasPostPurchaseAccess } from './helpers';
import { CelebrationIcon } from './components/icons';
import { CoursePartnerBanner } from './components/CoursePartnerBanner';
import { DomainPanel } from './components/DomainPanel';
import { PostPurchaseAccessDenied } from './components/PostPurchaseAccessDenied';
import { PostPurchaseLoadingScreen } from './components/PostPurchaseLoadingScreen';
import { SetupPanel } from './components/SetupPanel';
import { StepCard } from './components/StepCard';
import { StorefrontPanel } from './components/StorefrontPanel';
import styles from './PostPurchaseStep.scss';

// Storefront is always available and is shown by default, so on desktop it is
// also the panel we fall back to when a step is closed (keeps the two-column
// layout instead of collapsing the preview column).
const DEFAULT_PANEL_ID: PanelId = 'storefront';

export function PostPurchaseStep() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const metaSiteId = useMsId();
  const storeName = useSelectedStoreName();
  const {
    data: primarySiteUrlInfo,
    isLoading: isSiteUrlLoading,
    isError: isSiteUrlError,
    isFetched: isSiteUrlFetched,
  } = usePrimarySiteUrlInfo();
  const siteUrl = primarySiteUrlInfo?.url;
  const hasPremiumDomain = primarySiteUrlInfo?.hasPremiumDomain ?? false;
  const { data: acceptPaymentsSetupStatus } = useAcceptPaymentsSetup();
  const isAcceptPaymentsSetup = acceptPaymentsSetupStatus?.isAcceptPaymentsSetup ?? false;
  const [activePanelId, setActivePanelId] = useState<PanelId | null>(DEFAULT_PANEL_ID);
  const [mobileDrawerPanelId, setMobileDrawerPanelId] = useState<PanelId | null>(null);
  const [domainValue, setDomainValue] = useState('');
  const stepRefs = useRef<Record<PanelId, HTMLElement | null>>({
    storefront: null,
    domain: null,
    setup: null,
  });

  const dashboardUrl = useMemo(() => (metaSiteId ? buildDashboardUrl(metaSiteId) : undefined), [metaSiteId]);
  const partnerBrandName = getProviderConfig().brandName;
  const partnerCourseUrl = getSuccessUrl();
  const canAccessPostPurchase = hasPostPurchaseAccess(metaSiteId, siteUrl, isSiteUrlError, isSiteUrlFetched);
  const suggestedDomain = getSuggestedDomain(siteUrl, storeName);
  const visibleSteps = useMemo(
    () =>
      getPostPurchaseSteps(t).map((step) => {
        if (step.id === 'domain' && hasPremiumDomain) {
          return {
            ...step,
            done: true,
            description: t('post_purchase.steps.domain.done_description'),
            cta: t('post_purchase.cta.done'),
          };
        }

        if (step.id === 'setup' && isAcceptPaymentsSetup) {
          return {
            ...step,
            done: true,
            description: t('post_purchase.steps.setup.done_description'),
            cta: t('post_purchase.cta.done'),
          };
        }

        return step;
      }),
    [hasPremiumDomain, isAcceptPaymentsSetup, t],
  );
  const areAllSetupTasksComplete = visibleSteps.every((step) => step.done);

  useEffect(() => {
    if (siteUrl && !domainValue && suggestedDomain) {
      setDomainValue(suggestedDomain);
    }
  }, [domainValue, siteUrl, suggestedDomain]);

  useEffect(() => {
    if (!isMobile) {
      setMobileDrawerPanelId(null);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || !mobileDrawerPanelId) {
      return undefined;
    }

    const selectedStep = stepRefs.current[mobileDrawerPanelId];
    if (!selectedStep) {
      return undefined;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      selectedStep.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [isMobile, mobileDrawerPanelId]);

  if (!metaSiteId) {
    return <PostPurchaseAccessDenied />;
  }

  if (isSiteUrlLoading) {
    return <PostPurchaseLoadingScreen />;
  }

  if (!canAccessPostPurchase || !siteUrl) {
    return <PostPurchaseAccessDenied />;
  }

  const resolvedSiteUrl = siteUrl;

  function openPanel(panelId: PanelId) {
    setActivePanelId(panelId);

    if (isMobile) {
      setMobileDrawerPanelId(panelId);
    }
  }

  function closePanel(panelId?: PanelId) {
    const resolvedPanelId = panelId ?? activePanelId ?? mobileDrawerPanelId;
    if (resolvedPanelId) {
      postPurchaseBi.stepClose(resolvedPanelId);
    }

    if (isMobile) {
      setMobileDrawerPanelId(null);
      return;
    }

    // Keep the desktop two-column layout: fall back to the default storefront
    // preview instead of clearing the panel (which would collapse the column).
    setActivePanelId(DEFAULT_PANEL_ID);
  }

  function handleStepSelect(panelId: PanelId) {
    postPurchaseBi.stepSelect(panelId);
    openPanel(panelId);
  }

  function handleStepAction(event: React.MouseEvent, panelId: PanelId) {
    event.stopPropagation();
    postPurchaseBi.stepOpen(panelId);
    openPanel(panelId);
  }

  function renderPanel(panelId: PanelId) {
    if (panelId === 'storefront') {
      return <StorefrontPanel previewUrl={resolvedSiteUrl} siteUrl={resolvedSiteUrl} isLoading={isSiteUrlLoading} />;
    }

    if (panelId === 'domain') {
      return (
        <DomainPanel
          domainValue={domainValue}
          hasPremiumDomain={hasPremiumDomain}
          isMobile={isMobile}
          suggestedDomain={suggestedDomain}
          metaSiteId={metaSiteId}
          onDomainValueChange={setDomainValue}
        />
      );
    }

    return <SetupPanel isAcceptPaymentsSetup={isAcceptPaymentsSetup} isMobile={isMobile} metaSiteId={metaSiteId} />;
  }

  return (
    <div className={styles.postPurchase}>
      <div className={styles.postPurchaseSplit}>
        <main className={styles.postPurchaseMain}>
          <div className={styles.postPurchaseMainInner}>
            <section className={styles.postPurchaseHero} aria-labelledby="post-purchase-title">
              <CelebrationIcon />
              <Heading as="h1" size="extraLarge" id="post-purchase-title">
                {areAllSetupTasksComplete
                  ? t('post_purchase.all_set.title')
                  : `${t('post_purchase.title')} ${t('post_purchase.title_strong')}`}
              </Heading>
              <Button
                as="a"
                href={resolvedSiteUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={`Open ${getDisplayUrl(resolvedSiteUrl)}`}
                onClick={() => postPurchaseBi.siteLinkClick()}
                priority="secondary"
                size="small"
              >
                {getDisplayUrl(resolvedSiteUrl)}
              </Button>
              {areAllSetupTasksComplete ? (
                dashboardUrl && (
                  <Button
                    as="a"
                    href={dashboardUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    priority="secondary"
                    size="small"
                  >
                    {t('post_purchase.all_set.dashboard_cta')}
                  </Button>
                )
              ) : (
                <Text secondary className={styles.postPurchaseHeroSubtitle}>
                  {t('post_purchase.subtitle')}
                </Text>
              )}
            </section>

            <section className={styles.postPurchaseSteps} aria-label={t('post_purchase.tasks_label')}>
              {visibleSteps.map((step) => (
                <StepCard
                  key={step.id}
                  step={step}
                  isSelected={activePanelId === step.id}
                  inlinePanel={isMobile && mobileDrawerPanelId === step.id ? renderPanel(step.id) : undefined}
                  isPanelOpen={isMobile ? mobileDrawerPanelId === step.id : activePanelId === step.id && !step.done}
                  cardRef={(node) => {
                    stepRefs.current[step.id] = node;
                  }}
                  showInlineDetails={!isMobile}
                  onSelect={() => handleStepSelect(step.id)}
                  onAction={(event) => handleStepAction(event, step.id)}
                  domainValue={domainValue}
                  metaSiteId={metaSiteId}
                  suggestedDomain={suggestedDomain}
                  onDomainValueChange={setDomainValue}
                  onClosePanel={() => closePanel(step.id)}
                />
              ))}
            </section>

            {partnerBrandName && <CoursePartnerBanner brandName={partnerBrandName} actionUrl={partnerCourseUrl} />}
          </div>
        </main>

        {!isMobile && activePanelId && (
          <aside className={styles.postPurchasePreview} aria-label={t('post_purchase.preview_label')}>
            <div className={styles.postPurchasePreviewContent}>{renderPanel(activePanelId)}</div>
          </aside>
        )}
      </div>
    </div>
  );
}
